import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronRight,
  SearchX,
  Users,
  Building2,
  MessageSquare,
  ClipboardList,
  BarChart3,
  Settings,
  Shield,
  FileText,
  Bell,
  Paperclip,
  Zap,
  GitBranch,
  Clock,
  Tag,
  Layers,
  Mail,
  Target,
  TrendingUp,
  AlertTriangle,
  BookOpen,
  Briefcase,
  Database,
} from "lucide-react";

/* Metadata-driven nav has no per-entity icon from the backend, so pick a
 * reasonable icon from the module's label/table name. First match wins. */
const ICON_RULES = [
  [/user|employee|people|staff/i, Users],
  [/department|team|organi[sz]ation/i, Building2],
  [/interview/i, MessageSquare],
  [/question/i, ClipboardList],
  [/analytic|dashboard|report|metric|score|\bindex\b/i, BarChart3],
  [/setting|config|propert/i, Settings],
  [/role|permission|acl|access/i, Shield],
  [/log|audit|history/i, FileText],
  [/notification|alert/i, Bell],
  [/attachment|file|upload/i, Paperclip],
  [/action|automation|rule|trigger/i, Zap],
  [/flow|workflow|business_rule/i, GitBranch],
  [/job|schedule|cron/i, Clock],
  [/tag|label|categor/i, Tag],
  [/table|field|view|nav_/i, Layers],
  [/campaign|email|mail/i, Mail],
  [/goal|target|okr/i, Target],
  [/trend|growth/i, TrendingUp],
  [/risk|warning|churn/i, AlertTriangle],
  [/template|document|\bdoc\b/i, BookOpen],
  [/position|title|role_/i, Briefcase],
];

export const moduleIcon = (label, tableName) => {
  const haystack = `${label || ""} ${tableName || ""}`;
  const hit = ICON_RULES.find(([regex]) => regex.test(haystack));
  return hit ? hit[1] : Database;
};

/**
 * NavigationView
 * - Groups (apps) are collapsed by default
 * - Click/Enter/Space on group header toggles modules
 * - ARIA: button-like header with aria-expanded and aria-controls
 * - Search auto-expands groups with matches (visual, not persisted)
 * - Persists expanded groups in localStorage between sessions
 */
const STORAGE_KEY = "nav.expandedGroups";
const HEADER_STORAGE_KEY = "nav.collapsedHeaders";

const NavigationView = ({ onTableClick, searchTerm = "", collapsed = false }) => {
  const [navigation, setNavigation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const ids = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(ids) ? ids : []);
    } catch {
      return new Set();
    }
  });

  // Headers default to expanded -- this tracks which ones were explicitly
  // collapsed, rather than which are expanded, so a header added later
  // (e.g. a new nav header row) shows its modules by default.
  const [collapsedHeaders, setCollapsedHeaders] = useState(() => {
    try {
      const raw = localStorage.getItem(HEADER_STORAGE_KEY);
      const ids = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(ids) ? ids : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(HEADER_STORAGE_KEY, JSON.stringify([...collapsedHeaders]));
    } catch {}
  }, [collapsedHeaders]);

  const toggleHeader = useCallback((headerId) => {
    setCollapsedHeaders((prev) => {
      const next = new Set(prev);
      if (next.has(headerId)) next.delete(headerId);
      else next.add(headerId);
      return next;
    });
  }, []);

  const token = localStorage.getItem("token");

  // persist expanded groups when changed
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...expandedGroups]));
    } catch {}
  }, [expandedGroups]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/navigation`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!mounted) return;
        setNavigation(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("❌ Failed to load navigation:", err);
        if (mounted) setNavigation([]);
      })
      .finally(() => mounted && setLoading(false));

    return () => (mounted = false);
  }, [token]);

const filtered = useMemo(() => {
  const q = (searchTerm || "").trim().toLowerCase();
  if (!q) return navigation;

  return navigation
    .map((group) => {
      const groupLabel = String(group.label || "");
      const groupMatch = groupLabel.toLowerCase().includes(q);
      const modulesAll = group.modules || [];
      const headersAll = group.headers || [];

      // If the app (group) matches -> keep everything as-is.
      // Else -> keep only modules (headerless, and within each header) that match.
      const modules = groupMatch
        ? modulesAll
        : modulesAll.filter((m) => String(m.label || "").toLowerCase().includes(q));

      const headers = headersAll
        .map((h) => ({
          ...h,
          modules: groupMatch
            ? h.modules || []
            : (h.modules || []).filter((m) => String(m.label || "").toLowerCase().includes(q)),
        }))
        .filter((h) => groupMatch || h.modules.length);

      return {
        ...group,
        modules,
        headers,
        __matchByGroup: groupMatch, // internal flag we pass down
      };
    })
    // Keep groups that either matched by name OR have matching modules/headers
    .filter((g) => g.__matchByGroup || (g.modules && g.modules.length) || (g.headers && g.headers.length));
}, [navigation, searchTerm]);


  const toggleGroup = useCallback((groupIdOrLabel) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupIdOrLabel)) next.delete(groupIdOrLabel);
      else next.add(groupIdOrLabel);
      return next;
    });
  }, []);

  return (
    <nav
      aria-label="Primary"
      className="nav-scroll"
      style={{
        ...styles.container,
        width: collapsed ? 72 : 260,
      }}
    >
      {/* subtle left rail accent */}
      <div aria-hidden style={styles.leftRail} />

      {loading ? (
        <SidebarSkeleton collapsed={collapsed} />
      ) : filtered.length === 0 ? (
        <div style={styles.emptyWrap}>
          <SearchX size={18} color="rgba(229,231,235,.6)" style={{ marginBottom: 6 }} />
          <div style={styles.emptyBadge}>No matches</div>
          <div style={styles.emptyHint}>Try a different search.</div>
        </div>
      ) : (
        filtered.map((group) => {
  const groupId = group.id || group.label;
  const matchByGroup = !!group.__matchByGroup;

  // If searching, expand when the app name matches OR it has module matches.
  const isExpandedWhenSearching = Boolean(searchTerm) && (matchByGroup || (group.modules || []).length > 0);
  const isExpanded = isExpandedWhenSearching || expandedGroups.has(groupId);

  return (
    <NavGroup
      key={groupId}
      id={groupId}
      label={group.label}
      modules={group.modules || []}
      headers={group.headers || []}
      collapsed={collapsed}
      expanded={isExpanded}
      onToggle={() => toggleGroup(groupId)}
      onTableClick={onTableClick}
      searching={Boolean(searchTerm)}
      matchByGroup={matchByGroup} // <-- new
      collapsedHeaders={collapsedHeaders}
      onToggleHeader={toggleHeader}
    />
  );
})

      )}
    </nav>
  );
};

const NavGroup = ({
  id,
  label,
  modules,
  headers = [],
  collapsed,
  expanded,
  onToggle,
  onTableClick,
  searching,
  collapsedHeaders,
  onToggleHeader,
}) => {
  // When the whole sidebar is collapsed, we just show icons; clicking a module still works.
  // We don’t render the toggle affordance in collapsed rail.
  const listId = `nav-group-${String(id).replace(/\s+/g, "-")}`;
  const totalCount = modules.length + headers.reduce((sum, h) => sum + (h.modules?.length || 0), 0);

  return (
    <section style={styles.group} aria-label={label}>
      {!collapsed && (
        <header
          className="nav-group-header"
          style={{ ...styles.groupHeader, cursor: "pointer" }}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={!searching ? onToggle : undefined}
          onKeyDown={(e) => {
            if (searching) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onToggle();
            }
           if (e.key === "Escape") e.currentTarget.blur?.();
          }}
          title={searching ? "Expanded due to search" : expanded ? "Collapse" : "Expand"}
        >
          <span style={{ ...styles.groupDot, display: "inline-flex", transform: expanded ? "rotate(90deg)" : "none" }} aria-hidden>
            <ChevronRight size={13} />
          </span>
          <span style={styles.groupLabel}>{label}</span>
          <span style={styles.groupCount} aria-hidden>
            {totalCount}
          </span>
        </header>
      )}

      <Collapsible show={collapsed || expanded}>
        <ul id={listId} style={styles.list} role="list" aria-hidden={!(collapsed || expanded)}>
          {modules.map((mod) => (
            <NavItem
              key={mod.table_name?.name || mod.target_path || mod.label}
              label={mod.label}
              tableName={mod.table_name?.name}
              targetPath={mod.target_path}
              collapsed={collapsed}
              openInNewTab={mod.open_in_new_tab}
              onClick={() => {
                if (onTableClick && mod.table_name?.name) onTableClick(mod.table_name.name);
              }}
            />
          ))}
          {headers.map((h) => {
            const headerModules = h.modules || [];
            if (headerModules.length === 0) return null;
            const headerCollapsed = collapsedHeaders?.has(h.id);
            const headerExpanded = collapsed || !headerCollapsed;

            return (
              <li key={h.id || h.label} style={{ padding: 0, listStyle: "none" }}>
                {!collapsed && (
                  <button
                    type="button"
                    className="nav-header-toggle"
                    style={styles.headerToggle}
                    onClick={() => onToggleHeader && h.id && onToggleHeader(h.id)}
                    aria-expanded={headerExpanded}
                    title={headerCollapsed ? "Expand" : "Collapse"}
                  >
                    <span
                      aria-hidden
                      style={{
                        display: "inline-flex",
                        transform: headerCollapsed ? "none" : "rotate(90deg)",
                        transition: "transform .12s ease",
                      }}
                    >
                      <ChevronRight size={10} />
                    </span>
                    <span>{h.label}</span>
                  </button>
                )}
                <Collapsible show={headerExpanded}>
                  <ul style={{ ...styles.list, margin: 0 }} role="list">
                    {headerModules.map((mod) => (
                      <NavItem
                        key={mod.table_name?.name || mod.target_path || mod.label}
                        label={mod.label}
                        tableName={mod.table_name?.name}
                        targetPath={mod.target_path}
                        collapsed={collapsed}
                        openInNewTab={mod.open_in_new_tab}
                        indented
                        onClick={() => {
                          if (onTableClick && mod.table_name?.name) onTableClick(mod.table_name.name);
                        }}
                      />
                    ))}
                  </ul>
                </Collapsible>
              </li>
            );
          })}
        </ul>
      </Collapsible>
    </section>
  );
};

const NavItem = ({ label, tableName, targetPath, collapsed, openInNewTab, indented, onClick }) => {
  const location = useLocation();
  const to = tableName ? `/dashboard/${tableName}/list_view` : targetPath || "#";

  const isActive = useMemo(() => {
    try {
      if (!to || to === "#") return false;
      return location.pathname.startsWith(to);
    } catch {
      return false;
    }
  }, [location.pathname, to]);

  const Icon = useMemo(() => moduleIcon(label, tableName), [label, tableName]);

  return (
    <li style={{ padding: 0 }}>
      <Link
        to={to}
        onClick={onClick}
        aria-label={label}
        aria-current={isActive ? "page" : undefined}
        title={collapsed ? label : undefined}
        target={openInNewTab ? "_blank" : undefined}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        style={{
          ...styles.itemBase,
          ...(collapsed ? styles.itemCollapsed : styles.itemExpanded),
          ...(indented && !collapsed ? styles.itemIndented : {}),
          ...(isActive ? styles.itemActive : {}),
        }}
        className="nav-item"
      >
        {/* active accent bar — only element signaling "current page" */}
        {!collapsed && (
          <span
            aria-hidden
            style={{ ...styles.activeBar, transform: isActive ? "scaleY(1)" : "scaleY(0)" }}
          />
        )}

        <span
          aria-hidden
          style={{
            ...styles.iconChip,
            ...(isActive ? styles.iconChipActive : {}),
          }}
        >
          <Icon size={13} strokeWidth={2.25} />
        </span>

        {/* label (hidden in collapsed) */}
        {!collapsed && <span style={styles.itemLabel}>{label}</span>}
      </Link>
    </li>
  );
};

/* ---------- lightweight collapsible with height animation ---------- */
const Collapsible = ({ show, children, duration = 160 }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(show ? "auto" : 0);
  const [rendered, setRendered] = useState(show);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (show) {
      setRendered(true);
      const natural = el.scrollHeight;
      requestAnimationFrame(() => {
        setHeight(natural);
        const toAuto = setTimeout(() => setHeight("auto"), duration + 30);
        return () => clearTimeout(toAuto);
      });
    } else {
      const natural = el.scrollHeight;
      setHeight(natural);
      requestAnimationFrame(() => setHeight(0));
      const hideTimer = setTimeout(() => setRendered(false), duration + 30);
      return () => clearTimeout(hideTimer);
    }
  }, [show, duration]);

  if (!rendered && !show) return null;

  return (
    <div
      ref={ref}
      style={{
        overflow: "hidden",
        transition: `height ${duration}ms ease`,
        height,
      }}
    >
      {children}
    </div>
  );
};

/* ---------- skeleton ---------- */
const SidebarSkeleton = ({ collapsed }) => {
  const lines = collapsed ? 8 : 6;
  return (
    <div>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} style={styles.skelRow}>
          <div style={styles.skelDot} />
          {!collapsed && <div style={styles.skelLine} />}
        </div>
      ))}
    </div>
  );
};

const styles = {
  container: {
    position: "relative",
    background: "linear-gradient(180deg, #111827 0%, #0f172a 100%)",
    color: "#e5e7eb",
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarGutter: "stable",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    display: "flex",
    flexDirection: "column",
    padding: "12px 8px 12px 12px",
    boxShadow: "4px 0 16px rgba(2,6,23,0.25)",
    transition: "width .18s ease",
  },

  leftRail: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 2,
    background: "linear-gradient(180deg, rgba(45,212,191,.8), rgba(45,212,191,0))",
  },

  group: { marginBottom: 12, paddingBottom: 4 },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "rgba(255,255,255,0.85)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: ".02em",
    textTransform: "none",
    padding: "8px 10px",
    marginBottom: 6,
    borderRadius: 10,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    transition: "background .12s ease, box-shadow .12s ease",
  },
  groupDot: { fontSize: 12, color: "#5eead4" },
  groupLabel: { lineHeight: 1, flex: 1 },
  groupCount: {
    fontVariantNumeric: "tabular-nums",
    fontSize: 11,
    padding: "2px 6px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.06)",
    color: "#d1d5db",
    fontWeight: 700,
  },

  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: 4,
  },

  itemBase: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 10,
    textDecoration: "none",
    color: "#e5e7eb",
    borderRadius: 10,
    padding: "8px 10px 8px 12px",
    transition: "background .12s ease, transform .12s ease, box-shadow .12s ease",
  },
  itemExpanded: {},
  itemIndented: { paddingLeft: 24 },
  headerToggle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    width: "100%",
    padding: "10px 10px 4px 12px",
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color: "rgba(229,231,235,.4)",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    transition: "color .12s ease",
  },
  itemCollapsed: {
    justifyContent: "center",
    padding: 8,
    width: 44,
    height: 44,
    margin: "0 auto",
  },

  itemActive: {
    background: "rgba(20,184,166,0.14)",
  },

  iconChip: {
    width: 24,
    height: 24,
    minWidth: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    color: "#99f6e4",
    background: "rgba(255,255,255,0.06)",
    transition: "background .12s ease, color .12s ease",
  },
  iconChipActive: {
    color: "#04211d",
    background: "linear-gradient(180deg, #5eead4 0%, #2dd4bf 100%)",
    boxShadow: "0 1px 1px rgba(2,6,23,0.25)",
  },

  itemLabel: {
    fontSize: 13.5,
    fontWeight: 600,
    color: "#f3f4f6",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  },

  activeBar: {
    position: "absolute",
    left: 0,
    top: "20%",
    bottom: "20%",
    width: 3,
    borderRadius: 999,
    background: "#2dd4bf",
    transformOrigin: "center",
    transition: "transform .16s ease",
  },

  emptyWrap: {
    marginTop: 12,
    padding: 12,
    border: "1px dashed rgba(255,255,255,0.12)",
    borderRadius: 10,
    textAlign: "center",
    background: "rgba(255,255,255,0.02)",
  },
  emptyBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.06)",
    color: "#d1d5db",
    fontSize: 12,
    fontWeight: 700,
  },
  emptyHint: { marginTop: 6, color: "rgba(229,231,235,0.7)", fontSize: 12 },

  skelRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.06)",
    marginBottom: 6,
  },
  skelDot: {
    width: 22,
    height: 22,
    borderRadius: 8,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.08), rgba(255,255,255,0.16), rgba(255,255,255,0.08))",
    backgroundSize: "200% 100%",
    animation: "nav-skel 1.2s infinite",
  },
  skelLine: {
    height: 10,
    width: "70%",
    borderRadius: 999,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.08), rgba(255,255,255,0.16), rgba(255,255,255,0.08))",
    backgroundSize: "200% 100%",
    animation: "nav-skel 1.2s infinite",
  },
};

/* Inject keyframes once + reduced-motion */
if (typeof document !== "undefined" && !document.getElementById("nav-skel-kf")) {
  const style = document.createElement("style");
  style.id = "nav-skel-kf";
  style.innerHTML = `
    @keyframes nav-skel {
      0% { background-position: -120% 0; }
      100% { background-position: 120% 0; }
    }
    .nav-item:focus-visible {
      outline: none !important;
      box-shadow: 0 0 0 4px rgba(45,212,191,.25), inset 0 0 0 1px rgba(255,255,255,.18);
    }
    .nav-item:hover { transform: translateX(2px); background: rgba(255,255,255,0.05); }
    .nav-group-header:hover { background: rgba(255,255,255,0.07) !important; border-color: rgba(45,212,191,0.25) !important; }
    .nav-group-header:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(45,212,191,.3);
    }
    .nav-header-toggle:hover { color: rgba(229,231,235,.75) !important; }
    .nav-header-toggle:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(45,212,191,.3);
      border-radius: 6px;
    }
    @media (prefers-reduced-motion: reduce) {
      .nav-item, .nav-skel { transition: none !important; animation: none !important; }
    }

    /* modern, always-visible (not hover-only) scrollbar for the navigator —
       deliberately opaque since not every user has a mouse to hover with. */
    .nav-scroll {
      scroll-behavior: smooth;
      scrollbar-width: auto;
      scrollbar-color: rgba(94,234,212,.75) rgba(255,255,255,.06);
    }
    .nav-scroll::-webkit-scrollbar { width: 10px; }
    .nav-scroll::-webkit-scrollbar-track {
      background: rgba(255,255,255,.06);
      border-radius: 999px;
    }
    .nav-scroll::-webkit-scrollbar-thumb {
      background: rgba(94,234,212,.75);
      border-radius: 999px;
      border: 2px solid #111827;
    }
    .nav-scroll::-webkit-scrollbar-thumb:hover { background: rgba(94,234,212,.95); }
  `;
  document.head.appendChild(style);
}

export default NavigationView;
